import React, { useCallback, useEffect, useState } from 'react';
import { Input, Modal, Switch, Tag, Select } from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import axios from 'axios';
import { toast } from 'react-toastify';
import config from '../../config';
import {
  NdoePage, NdoePageHeader, NdoeActionButton, NdoeLoading, NdoeError, NdoeEmpty,
} from '../../components/ndoe';
import { messageErreur } from '../../components/ndoe/chargement';
import './parametresCriteres.css';

const DOMAIN = config.REACT_APP_SERVER_DOMAIN;

/*
 * PARAMÈTRES DES CRITÈRES — créer soi-même le vocabulaire du catalogue.
 *
 * Jusqu'ici, ajouter un critère (« hauteur de tige », « doublure ») demandait
 * une migration, donc un développeur. La propriétaire pouvait renseigner les
 * critères existants, pas en inventer un. Cet écran ferme ce manque.
 *
 * ── CE QUE L'ÉCRAN REFUSE DE FAIRE À LA LÉGÈRE ──────────────────────────────
 *
 * Les clés étrangères sont en ON DELETE CASCADE : supprimer un critère efface
 * toutes les qualifications de modèles qui en dépendent. Le serveur compte donc
 * d'abord, refuse (409), et ne cède qu'à une confirmation explicite. L'écran
 * relaie ce refus TEL QUEL — il est écrit pour être lu, et le réécrire ici le
 * ferait diverger le jour où la règle changera.
 *
 * ── LE CODE NE SE MODIFIE PAS, ET L'ÉCRAN LE DIT ────────────────────────────
 *
 * `code` est lu par les règles de déduction automatique. Le champ n'est donc
 * offert qu'à la CRÉATION, et affiché en lecture seule ensuite. Un champ
 * modifiable qui casse silencieusement une déduction serait pire qu'un champ
 * absent.
 *
 * ── RENOMMER PLUTÔT QUE SUPPRIMER ───────────────────────────────────────────
 *
 * Une valeur mal nommée se RENOMME : les modèles pointent son identifiant, pas
 * son texte, donc ils suivent. Supprimer pour recréer perdrait toutes les
 * qualifications. Le message de refus du serveur le rappelle, et le bouton
 * « Renommer » est plus proéminent que la corbeille.
 */

const ParametresCriteres = () => {
  const [attributs, setAttributs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [creation, setCreation] = useState(null);
  const [envoi, setEnvoi] = useState(false);
  const [ajoutValeur, setAjoutValeur] = useState({});

  const charger = useCallback(async () => {
    setChargement(true); setErreur(null);
    try {
      const [a, c] = await Promise.all([
        axios.get(`${DOMAIN}/api/produit/attributs/admin`),
        axios.get(`${DOMAIN}/api/produit/categorie`).catch(() => ({ data: [] })),
      ]);
      setAttributs(Array.isArray(a.data && a.data.attributs) ? a.data.attributs : []);
      setCategories(Array.isArray(c.data) ? c.data : []);
    } catch (e) {
      setErreur(messageErreur(e, 'les critères'));
    } finally { setChargement(false); }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  // Le serveur porte les règles ET les messages : on les relaie tels quels.
  const agir = async (promesse, succes) => {
    setEnvoi(true);
    try {
      await promesse;
      toast.success(succes);
      await charger();
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.error || messageErreur(e, 'cette opération'));
      return false;
    } finally { setEnvoi(false); }
  };

  const creer = async () => {
    const f = creation || {};
    const valeurs = String(f.valeurs || '').split('\n').map((v) => v.trim()).filter(Boolean);
    const ok = await agir(
      axios.post(`${DOMAIN}/api/produit/attributs/admin`, {
        code: f.code, libelle: f.libelle, par_defaut: f.par_defaut !== false, valeurs,
      }),
      `Critère « ${f.libelle} » créé${valeurs.length ? ` avec ${valeurs.length} valeur(s)` : ''}.`,
    );
    if (ok) setCreation(null);
  };

  /*
    LA SUPPRESSION EN DEUX TEMPS.

    Premier appel sans confirmation : si des modèles dépendent du critère, le
    serveur refuse en 409 et DIT combien. On montre alors ce message tel quel et
    on demande une confirmation — pas un « Êtes-vous sûr ? » générique, qui ne
    donne aucune information sur ce qu'on s'apprête à perdre.
  */
  const supprimer = async (url, libelle) => {
    try {
      await axios.delete(url, { data: {} });
      toast.success(`« ${libelle} » supprimé.`);
      await charger();
    } catch (e) {
      const msg = e?.response?.data?.error;
      if (e?.response?.status === 409 && msg) {
        Modal.confirm({
          title: 'Cette suppression détruit des données',
          content: msg,
          okText: 'Supprimer quand même', okButtonProps: { danger: true },
          cancelText: 'Annuler',
          onOk: () => agir(axios.delete(url, { data: { confirme: true } }), `« ${libelle} » supprimé.`),
        });
      } else {
        toast.error(msg || messageErreur(e, 'la suppression'));
      }
    }
  };

  const renommer = (titre, valeurActuelle, onOk) => {
    let v = valeurActuelle;
    Modal.confirm({
      title: titre,
      content: <Input defaultValue={valeurActuelle} maxLength={80} onChange={(e) => { v = e.target.value; }} />,
      okText: 'Renommer', cancelText: 'Annuler',
      onOk: () => onOk(v),
    });
  };

  if (chargement) return <NdoePage><NdoeLoading rows={6} /></NdoePage>;
  if (erreur) {
    return (
      <NdoePage>
        <NdoeError title="Les critères n’ont pas pu être chargés" text={erreur} onRetry={charger} />
      </NdoePage>
    );
  }

  return (
    <NdoePage className="pc">
      <NdoePageHeader
        eyebrow="Catalogue"
        title="Paramètres des critères"
        subtitle="Le vocabulaire utilisé pour décrire vos modèles"
        actions={(
          <NdoeActionButton variant="primary" icon={<PlusOutlined />} onClick={() => setCreation({ par_defaut: true })}>
            Nouveau critère
          </NdoeActionButton>
        )}
      />

      <p className="pc-intro">
        Un <b>critère</b> est une question posée sur chaque modèle (« forme du bout »),
        et ses <b>valeurs</b> sont les réponses possibles (« pointu », « rond »…).
        Ils servent à la recherche, aux filtres et à la qualification.
      </p>

      {!attributs.length ? (
        <NdoeEmpty icon="🏷️" title="Aucun critère" text="Créez le premier avec « Nouveau critère »." />
      ) : attributs.map((a) => (
        <section key={a.id_attribut} className={`pc-critere ${a.actif ? '' : 'is-inactif'}`}>
          <header>
            <div className="pc-titre">
              <b>{a.libelle}</b>
              {/* Le code est montré parce qu'il compte — les règles de déduction
                  le lisent — mais il n'est pas modifiable. */}
              <span className="pc-code num" title="Identifiant technique, non modifiable">{a.code}</span>
              {!a.actif && <Tag color="default">Masqué</Tag>}
              {a.produits > 0 && (
                <Tag color="blue">{a.produits} modèle{a.produits > 1 ? 's' : ''}</Tag>
              )}
            </div>
            <div className="pc-actions">
              <label className="pc-switch" title="Poser la question par défaut sur tous les modèles">
                <Switch size="small" checked={a.par_defaut} disabled={envoi}
                  onChange={(v) => agir(
                    axios.put(`${DOMAIN}/api/produit/attributs/${a.id_attribut}`, { par_defaut: v }),
                    v ? 'Question posée par défaut.' : 'Question posée seulement sur les catégories choisies.',
                  )} />
                Demandé par défaut
              </label>
              <label className="pc-switch" title="Un critère masqué n’est plus proposé, mais rien n’est effacé">
                <Switch size="small" checked={a.actif} disabled={envoi}
                  onChange={(v) => agir(
                    axios.put(`${DOMAIN}/api/produit/attributs/${a.id_attribut}`, { actif: v }),
                    v ? 'Critère réaffiché.' : 'Critère masqué — les valeurs déjà saisies sont conservées.',
                  )} />
                Actif
              </label>
              <NdoeActionButton size="sm" variant="ghost" icon={<EditOutlined />} disabled={envoi}
                onClick={() => renommer('Renommer le critère', a.libelle, (v) => agir(
                  axios.put(`${DOMAIN}/api/produit/attributs/${a.id_attribut}`, { libelle: v }),
                  'Critère renommé.',
                ))}>
                Renommer
              </NdoeActionButton>
              <NdoeActionButton size="sm" variant="danger" icon={<DeleteOutlined />} disabled={envoi}
                onClick={() => supprimer(`${DOMAIN}/api/produit/attributs/${a.id_attribut}`, a.libelle)}>
                Supprimer
              </NdoeActionButton>
            </div>
          </header>

          <div className="pc-valeurs">
            {!a.valeurs.length && (
              <p className="pc-avert">
                Ce critère n’a aucune valeur : il ne pourra pas être renseigné tant qu’on ne lui en donne pas.
              </p>
            )}
            {a.valeurs.map((v) => (
              <span key={v.id_valeur} className="pc-valeur">
                <span className="pc-valeur__lib">{v.libelle}</span>
                {v.produits > 0 && <small className="num" title={`Retenue sur ${v.produits} modèle(s)`}>{v.produits}</small>}
                <button type="button" title="Renommer — les modèles suivront"
                  onClick={() => renommer('Renommer la valeur', v.libelle, (x) => agir(
                    axios.put(`${DOMAIN}/api/produit/attributs/valeurs/${v.id_valeur}`, { libelle: x }),
                    'Valeur renommée — les modèles suivent.',
                  ))}>✎</button>
                <button type="button" className="pc-suppr" title="Supprimer"
                  onClick={() => supprimer(`${DOMAIN}/api/produit/attributs/valeurs/${v.id_valeur}`, v.libelle)}>×</button>
              </span>
            ))}
            <span className="pc-ajout">
              <Input size="small" placeholder="Ajouter une valeur…" maxLength={80}
                value={ajoutValeur[a.id_attribut] || ''}
                onChange={(e) => setAjoutValeur((m) => ({ ...m, [a.id_attribut]: e.target.value }))}
                onPressEnter={async () => {
                  const lib = (ajoutValeur[a.id_attribut] || '').trim();
                  if (!lib) return;
                  const ok = await agir(
                    axios.post(`${DOMAIN}/api/produit/attributs/${a.id_attribut}/valeurs`, { libelle: lib }),
                    `« ${lib} » ajoutée.`,
                  );
                  if (ok) setAjoutValeur((m) => ({ ...m, [a.id_attribut]: '' }));
                }} />
            </span>
          </div>

          {/* LES EXCEPTIONS : « ne pas demander le type de talon aux mocassins ».
              On n'affiche QUE les catégories qui s'écartent du défaut — une
              exception identique au défaut n'en est pas une, et le serveur la
              refuse d'ailleurs. */}
          <div className="pc-exceptions">
            <span className="pc-exceptions__label">
              {a.par_defaut ? 'Sauf pour ces catégories :' : 'Demandé seulement pour :'}
            </span>
            <Select
              mode="multiple" size="small" allowClear disabled={envoi}
              className="pc-select"
              placeholder={a.par_defaut ? 'Aucune exception' : 'Aucune catégorie'}
              value={a.exceptions.map((e) => e.id_categorie)}
              options={categories.map((c) => ({ value: c.id_categorie, label: c.nom_categorie }))}
              onChange={(ids) => agir(
                axios.put(`${DOMAIN}/api/produit/attributs/${a.id_attribut}/exceptions`, {
                  exceptions: (ids || []).map((id) => ({ id_categorie: id, demande: !a.par_defaut })),
                }),
                'Exceptions enregistrées.',
              )}
            />
          </div>
        </section>
      ))}

      <Modal
        open={!!creation}
        title="Nouveau critère"
        onCancel={() => setCreation(null)}
        onOk={creer}
        okText="Créer"
        cancelText="Annuler"
        confirmLoading={envoi}
        destroyOnClose
      >
        <div className="pc-form">
          <label>
            Nom affiché
            <Input autoFocus maxLength={80} placeholder="Hauteur de tige"
              value={creation?.libelle || ''}
              onChange={(e) => setCreation((f) => ({ ...f, libelle: e.target.value }))} />
          </label>
          <label>
            Identifiant technique
            <Input maxLength={39} placeholder="hauteur_tige"
              value={creation?.code || ''}
              onChange={(e) => setCreation((f) => ({ ...f, code: e.target.value }))} />
            <small>
              Minuscules, chiffres et « _ ». <b>Ne pourra plus être modifié</b> : les règles
              de reconnaissance automatique s’en servent.
            </small>
          </label>
          <label>
            Valeurs possibles — une par ligne
            <Input.TextArea rows={5} placeholder={'Basse\nMi-haute\nHaute'}
              value={creation?.valeurs || ''}
              onChange={(e) => setCreation((f) => ({ ...f, valeurs: e.target.value }))} />
            <small>Vous pourrez en ajouter d’autres ensuite.</small>
          </label>
          <label className="pc-switch">
            <Switch checked={creation?.par_defaut !== false}
              onChange={(v) => setCreation((f) => ({ ...f, par_defaut: v }))} />
            Poser la question sur tous les modèles
          </label>
        </div>
      </Modal>
    </NdoePage>
  );
};

export default ParametresCriteres;
